import { afterNextRender, DestroyRef, inject, Injectable, Injector } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, filter, map } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DOCUMENT } from '@angular/common';
import { OpcoService } from '@common/services/opco.service';
import { APP_CONFIG } from '@app/config/app-config';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsClient {

  private readonly appConfig = inject(APP_CONFIG);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private readonly opcoService = inject(OpcoService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);

  private readonly dataPropertyPageType = 'data-analytics-page-type';
  private readonly dataPropertyScope = 'data-analytics-scope';

  private readonly onDocumentClickListener = (event: MouseEvent) => this.onDocumentClick(event);

  constructor() {
    this.destroyRef.onDestroy(() => {
      window.removeEventListener('click', this.onDocumentClickListener);
    });
  }

  /**
   * Simple example to capture page views in an Angular application.
   * Replace app_id, environment, opco and locale with your own implementation.
   */
  capturePageViews() {
    this.router.events.pipe(
      takeUntilDestroyed(this.destroyRef),
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      // Strip query parameters and anchor tags to avoid multiple page_view events for the same page.
      map(event => event.urlAfterRedirects.split('?')[0]),
      distinctUntilChanged(),
    ).subscribe(() => {
      afterNextRender({
        read: (_) => {
          this.capture({
            event: 'page_view',
            app_id: 'qplanning',
            environment: this.appConfig.environment,
            page_type: this.document.querySelector(`[${this.dataPropertyPageType}]`)?.getAttribute(this.dataPropertyPageType) as AnalyticsPageType,
            opco: this.opcoService.getActiveOpco(),
            locale: this.translateService.currentLang,
          });
        }
      }, { injector: this.injector });
    });
  }

  /**
   * Captures mouse click events on relevant Zenith components.
   */
  captureMouseClicks() {
    window.addEventListener('click', this.onDocumentClickListener);
  }

  private onDocumentClick(clickEvent: MouseEvent): void {
    const element = clickEvent.target as Element;
    const tagName = element.tagName;
    const textContent = element.textContent?.trim();
    const toolTip = element.getAttribute('zn-tooltip');
    /*
     * Depending on how the Angular component is rendered, the scope value might be missing
     * because the element could have changed DOM position by the time this click event handler
     * is triggered. Examples are: zenith-drop-menu, zenith-popover, elements in a zenith-modal etc.
     */
    const scope = element.closest(`[${this.dataPropertyScope}]`)?.getAttribute(this.dataPropertyScope);

    let event;
    let type: AnalyticsInputType;
    let id = this.getNameOrId(element);
    let text = textContent || toolTip;
    let value;
    let position;

    switch (tagName) {
      /*
       * Captures all button clicks.
       */
      case  'BUTTON':
      case  'ZENITH-BUTTON': {
        event = 'button_click';
        break;
      }

      /*
       * Captures clicks on a <zenith-drop-menu-item>. Intentionally not tracking the
       * <zenith-drop-menu> for now.
       */
      case 'ZENITH-DROP-MENU-ITEM': {
        event = 'drop_menu_item_click';
        position = this.calculatePosition(element);
        break;
      }

      /*
       * Captures clicks on Zenith form components. Because 1) there is only a limited amount of
       * data we can track outside of Zenith and 2) some components rely on textual input instead
       * of clicks, the following components are not tracked:
       * - <zenith-input-date>
       * - <zenith-input-email>
       * - <zenith-input-number>
       * - <zenith-input-password>
       * - <zenith-input-text>
       * - <zenith-input-time>
       * - <zenith-textarea>
       */
      case 'ZENITH-CHECKBOX':
        event = 'input';
        type = 'checkbox';
        value = this.getValue(element);
        position = this.calculatePosition(element); // A zenith-checkbox can belong to a group.
        break;
      case 'ZENITH-PILL-SWITCH':
        event = 'input';
        type = 'pill-switch';
        text = undefined; // Because the label of a pill switch is defined outside the component.
        value = this.getValue(element);
        break;
      case 'ZENITH-RADIO-BUTTON':
        event = 'input';
        type = 'radio';
        value = this.getValue(element);
        break;
      case 'ZENITH-SELECT-ITEM':
        event = 'input';
        type = 'select';
        id = this.getNameOrId(element.parentElement);
        break;
      case 'ZENITH-TOGGLE-ITEM':
        event = 'input';
        type = 'toggle';
        break;
    }

    if (event) {
      this.capture({ event, scope, id, text, type, value, position });
    }
  }

  /**
   * Prefer `name` over `id` as the reporting identifier. Element IDs must be unique across the
   * entire page, so the enclosing scope inevitably leaks into the ID — requiring manual extraction
   * in the reporting tool. Using `name` sidesteps both problems: scope stays explicit, and the
   * same component can appear multiple times on a page without conflict.
   */
  private getNameOrId(element: Element) {
    return element.attributes['name']?.value || element.attributes['zn-name']?.value || element.id;
  }

  private getValue(element: Element) {
    return element.attributes['zn-value']?.value;
  }

  /**
   * Calculates the position for elements belonging to a "group".
   */
  private calculatePosition(element: Element) {
    // We cannot use element.parentElement.children here because it can be a mix of tags.
    const allSiblings = element.parentElement.querySelectorAll(`:scope > ${element.tagName}`);

    let position = 0;
    for (let i = 0; i < allSiblings.length; i++) {
      if (allSiblings.item(i) === element) {
        position = i;
        break;
      }
    }

    return position;
  }

  capture(event: any) {
    const dataLayer = (window as any).dataLayer as object[];
    if (dataLayer) {
      dataLayer.push(event);
    }
  }

}

export interface AnalyticsPageView {
  readonly event: 'page_view';
  readonly app_id: string;
  readonly environment: string;
  readonly opco: string;
  readonly locale: string;
  readonly page_type: AnalyticsPageType;
}

export enum AnalyticsPageType {
  OVERVIEW = 'overview',
  DETAIL = 'detail',
}

export interface AnalyticsBaseEvent {
  readonly event: 'button_click' | 'drop_menu_item_click' | 'input';
  readonly scope: string;
  readonly id: string;
  readonly text?: string;
}

export interface AnalyticsButtonClick extends AnalyticsBaseEvent {
  readonly event: 'button_click';
}

export interface AnalyticsDropMenuItemClick extends AnalyticsBaseEvent {
  readonly event: 'drop_menu_item_click';
  readonly position: number;
}

export interface AnalyticsInput extends AnalyticsBaseEvent {
  readonly event: 'input';
  readonly type: AnalyticsInputType;
  readonly value: string;
  readonly position?: number;
}

export type AnalyticsInputType =
  'checkbox' | 'select' | 'multi-select' | 'pill-switch' | 'radio' | 'toggle';

